import { inject, Injectable } from "@angular/core";
import { Capacitor } from "@capacitor/core";
import Stripe from "stripe";
import { getDefaultSubscription } from "@bitwarden/web-vault/app/models/data/subscription.data";
import { DataRepositoryService } from "@bitwarden/web-vault/app/services/DataRepository/data-repository.service";

import { StripeConfigData } from "../../models/data/stripe-config.data";
import { GlossRoutes, MainSettingsRoute } from "@bitwarden/web-vault/app/models/enum/routes.enum";
import {
  PaymentMethod,
  PaymentHistoryResponse,
} from "@bitwarden/web-vault/app/models/types/payment.types";

@Injectable({
  providedIn: "root",
})
export class StripeConfigService extends StripeConfigData {
  private readonly CURRENCY_USD = "usd";
  private dataRepositoryService = inject(DataRepositoryService);

  constructor() {
    super();
  }

  async createCheckoutSessions(payload: Stripe.Checkout.SessionCreateParams): Promise<any> {
    const endpoint = this.config.endpoint["checkout_sessions"];
    // this is a authed call to match the user info with the backend
    return await this.dataRepositoryService.send("POST", endpoint, payload, true, true, this.url);
  }

  async getCheckoutSessions(sessionId: string): Promise<any> {
    const endpoint = this.config.endpoint["checkout_sessions"];

    return await this.dataRepositoryService.send(
      "GET",
      `${endpoint}/${sessionId}`,
      null,
      false,
      true,
      this.url,
    );
  }

  async getSubscription() {
    return Promise.resolve(getDefaultSubscription());
    //
    // try {
    //   const profile = await this.apiService.getProfile();
    //   const getSubscription = this.config.endpoint["get_subscription"];
    //   const endpoint = `${getSubscription}/${profile.email}`;
    //   return await this.dataRepositoryService.send(
    //     "GET",
    //     `${endpoint}`,
    //     null,
    //     true,
    //     true,
    //     this.url
    //   );
    // } catch (e) {
    //   return Promise.resolve(getDefaultSubscription());
    // }
  }

  async cancelSubscription(subscriptionId: string) {
    const endpoint = this.subscriptionConfig.endpoint["cancel_subscription"];
    const payload = { subscriptionId };

    return await this.dataRepositoryService.send(
      "PATCH",
      `${endpoint}`,
      payload,
      true,
      true,
      this.url,
    );
  }

  setPayload(stripePriceId: string, withCard: boolean): Stripe.Checkout.SessionCreateParams {
    const membershipSuccessUrl = `${this.redirectUrl}/#/${GlossRoutes.Views}`;
    // Backing out of Stripe lands on Bundles, where the checkout starts from (#6583). That is a
    // cold load, so InitialRedirectGuard must let this route through.
    const membershipCancelUrl = `${this.redirectUrl}/#/${GlossRoutes.Settings}/${MainSettingsRoute.Plans}`;

    return {
      success_url: membershipSuccessUrl,
      cancel_url: membershipCancelUrl,
      allow_promotion_codes: true,
      line_items: [
        {
          price: stripePriceId,
          quantity: 1,
        },
      ],
      mode: "subscription",
      subscription_data: {
        trial_period_days: 14,
        trial_settings: {
          end_behavior: {
            // cannot create a subscription schedule from a subscription is set to pause or cancel at period end
            missing_payment_method: "create_invoice",
          },
        },
      },
      payment_method_collection: withCard ? "always" : "if_required",
      metadata: {
        stage: this.config.stage,
      },
    };
  }

  /**
   * Add payment details for the user (e.g., card info via Stripe)
   * @param payload Stripe payment method details or token
   */
  async addPaymentDetails(payload: any): Promise<void> {
    const endpoint = this.config.endpoint["add_payment_details"];
    return await this.dataRepositoryService.send("POST", endpoint, payload, true, true, this.url);
  }

  async getPaymentMethods(): Promise<PaymentMethod[]> {
    const endpoint = this.config.endpoint["get_payment_details_default"];
    return await this.dataRepositoryService.send("GET", endpoint, null, true, true, this.url);
  }

  async deletePaymentMethod(): Promise<void> {
    const endpoint = this.config.endpoint["get_payment_details_default"];
    return await this.dataRepositoryService.send("DELETE", endpoint, null, true, true, this.url);
  }

  async getPaymentHistory(): Promise<PaymentHistoryResponse> {
    const endpoint = this.config.endpoint["get_payment_history"];
    return await this.dataRepositoryService.send("GET", endpoint, null, true, true, this.url);
  }

  async triggerFreeTrial(priceId: string = this.config.price.monthly): Promise<any> {
    const endpoint = this.config.endpoint["trigger_free_trial"];
    // iOS trials must end without a Stripe invoice; the server reads this to pick the end behavior.
    const platform = Capacitor.getPlatform();
    // These are the sandbox price IDs from Stripe for Brian's sandbox APIkey. Make sure to use the correct ones for your environment.
    return await this.dataRepositoryService.send(
      "POST",
      endpoint,
      { priceId, platform },
      true,
      true,
      this.url,
    );
  }
}
